import { useState, useEffect, useRef, type MutableRefObject } from 'react';
import { StreamMediaItem } from '~/stores/stream.store';
import { mediaStore } from '~/stores';
import { logger } from '~/services/logging/logger';
import { otaLog } from '~/services/ota/OtaDebugLogger';

export type PlaybackSource = 'cache' | 'remote' | 'pending';

/**
 * CORS só em URLs remotas (R2/CDN). `crossOrigin="anonymous"` em URLs
 * `http://localhost/_capacitor_file_/...` quebra playback no WebView Android.
 */
export function applyMediaCrossOrigin(
  el: HTMLVideoElement | HTMLImageElement,
  source: PlaybackSource,
): void {
  if (source === 'remote') {
    el.crossOrigin = 'anonymous';
  } else {
    el.removeAttribute('crossorigin');
  }
}

export function mediaCrossOriginProp(
  source: PlaybackSource,
): 'anonymous' | undefined {
  return source === 'remote' ? 'anonymous' : undefined;
}

export interface UsePlayableUrlResult {
  url: string | null;
  source: PlaybackSource;
  isLoading: boolean;
  error: string | null;
}

export interface PlayableUrlOverride {
  mediaId: string;
  url: string;
  source: PlaybackSource;
}

/**
 * Hook that resolves the playable URL for a media item.
 *
 * Checks cache first, falls back to remote URL (stream) if not cached.
 *
 * Platform-specific behavior:
 * - Tizen: Uses file:// URLs (TizenMediaCache)
 * - Browser (HTTPS): Uses blob URLs (MediaCache/Cache API)
 * - Browser (HTTP): Uses blob URLs (IndexedDBCache)
 *
 * @param media - The media item to resolve URL for
 * @returns Object with url, source ('cache' | 'remote' | 'pending'), loading state, and error
 *
 * @example
 * ```tsx
 * const { url, source, isLoading } = usePlayableUrl(currentMedia);
 *
 * return <video src={url} />;
 * ```
 */
export interface UsePlayableUrlOptions {
  /**
   * Quando true, não faz upgrade remote→cache no meio da exibição.
   * Stream seta após onPlaying; evita reload que destrói MediaCodec (Tab A7).
   */
  deferCacheUpgradeRef?: MutableRefObject<boolean>;
}

export function usePlayableUrl(
  media: StreamMediaItem | null,
  override?: PlayableUrlOverride | null,
  options?: UsePlayableUrlOptions,
): UsePlayableUrlResult {
  const [url, setUrl] = useState<string | null>(null);
  const [source, setSource] = useState<PlaybackSource>('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the resolved media identity without ignoring URL changes after sync.
  const resolvedMediaKeyRef = useRef<string | null>(null);

  // 2026-06-13 (Renato): leitura síncrona do `source` atual sem incluir no
  // deps array do useEffect. Antes (1.0.173), `source` estava no deps e
  // criava LOOP: resolução 1 → setSource('remote') → effect re-rodava →
  // resolução 2 → setSource('cache') → effect re-rodava (!). Cada
  // setSource trocava o src do <video> e DESTRUÍA o MediaCodec ativo
  // (logs do Tab A7 mostraram OMX.qcom.video.decoder.hevc com lifetimeMs=84
  // — decoder morrendo em <100ms, vídeo nunca chega a tocar). A ref aqui
  // permite ler o source corrente sem virar dep do effect.
  const sourceRef = useRef<PlaybackSource>('pending');
  sourceRef.current = source;

  // 2026-06-12 (Renato): observar cacheStatus do mídia atual pra re-tentar
  // quando o MediaStore.init() carregar tardiamente (race no boot offline).
  // Se source='remote' agora mas cache fica pronto depois, esse cacheKey
  // muda e o useEffect re-roda → tenta cache de novo.
  const cacheStatusKey = media
    ? `${mediaStore.cacheStatus.get(media.id)?.status ?? 'none'}`
    : 'none';

  useEffect(() => {
    // If no media, reset state
    if (!media) {
      setUrl(null);
      setSource('pending');
      setIsLoading(false);
      setError(null);
      resolvedMediaKeyRef.current = null;
      return;
    }

    const mediaKey = `${media.id}:${media.url}`;

    // CRITICAL: If URL already resolved for this exact media URL, DO NOT re-resolve.
    // This prevents video restart on re-renders while still accepting URL changes after sync.
    //
    // EXCEPTION (2026-06-13, refinado): só re-resolver no cenário ESPECÍFICO
    // de "estava em remote (fallback temporário) e agora cache ficou pronto".
    // Sem essa exceção, o boot offline ficava preso em remote eternamente.
    // Sem o sourceRef (usar source direto), criava LOOP de re-resolução que
    // destruía o MediaCodec do <video> a cada troca de src.
    const shouldRetryForCacheReady =
      sourceRef.current === 'remote' &&
      cacheStatusKey === 'cached' &&
      !options?.deferCacheUpgradeRef?.current;
    if (resolvedMediaKeyRef.current === mediaKey && !shouldRetryForCacheReady) {
      return;
    }

    // Use override when the caller already has a resolved URL (e.g. preloaded)
    if (override && media.id === override.mediaId) {
      setUrl(override.url);
      setSource(override.source);
      setIsLoading(false);
      setError(null);
      resolvedMediaKeyRef.current = mediaKey;
      return;
    }

    // Resolve URL: cache first, fallback to remote
    async function resolveUrl() {
      // Capture non-null media value for TypeScript
      const currentMedia = media;
      if (!currentMedia) return;

      setIsLoading(true);
      setError(null);

      try {
        // Try cache first
        const localUrl = await mediaStore.getLocalUrlAsync(currentMedia);

        // 2026-06-13 — Defesa: só chama setUrl/setSource se REALMENTE muda.
        // React faz bailout em setState com valor idêntico, mas chamar setUrl
        // com URL "diferente apenas em query string" (presigned R2 refresh)
        // dispararia re-render desnecessário do <video>. Comparamos
        // estritamente; mudança = recria MediaCodec, custoso no Tab A7.
        if (localUrl) {
          logger.debug(`[usePlayableUrl] 📦 Using cache: ${currentMedia.name}`);
          // 2026-06-13 — Log nativo (sobrevive boot via OtaDebugBridge no
          // logcat). Critical pra diagnosticar boot offline em campo onde
          // chrome://inspect não tá acessível.
          otaLog.info('usePlayableUrl: resolvido CACHE', {
            mediaId: currentMedia.id.slice(0, 8),
            name: currentMedia.name?.slice(0, 30) ?? '?',
            localUrl: localUrl.slice(0, 120),
          });
          setUrl((prev) => (prev === localUrl ? prev : localUrl));
          setSource((prev) => (prev === 'cache' ? prev : 'cache'));
        } else {
          // Fallback pra stream remoto não é necessariamente problema —
          // pode ser a primeira reprodução antes do cache encher. INFO.
          logger.info(`[usePlayableUrl] 🌐 Using stream: ${currentMedia.name}`);
          otaLog.warn('usePlayableUrl: resolvido REMOTE (cache miss)', {
            mediaId: currentMedia.id.slice(0, 8),
            name: currentMedia.name?.slice(0, 30) ?? '?',
            cacheStatus: cacheStatusKey,
          });
          setUrl((prev) =>
            prev === currentMedia.url ? prev : currentMedia.url,
          );
          setSource((prev) => (prev === 'remote' ? prev : 'remote'));
        }

        setIsLoading(false);
        resolvedMediaKeyRef.current = `${currentMedia.id}:${currentMedia.url}`;
      } catch (err) {
        logger.error('[usePlayableUrl] Error resolving URL:', err);
        // Fallback to remote on error
        setUrl(currentMedia.url);
        setSource('remote');
        setError(err instanceof Error ? err.message : 'Unknown error');
        setIsLoading(false);
        resolvedMediaKeyRef.current = `${currentMedia.id}:${currentMedia.url}`;
      }
    }

    resolveUrl();
  }, [
    media?.id,
    media?.url,
    override?.mediaId,
    override?.url,
    override?.source,
    // 2026-06-12 — re-resolver quando cacheStatus muda (cobre race do boot
    // offline em que MediaStore.init() popula cacheStatus tardiamente).
    // 2026-06-13 — REMOVIDO `source` dos deps (criava loop que destruía o
    // MediaCodec). Leitura síncrona via sourceRef agora.
    cacheStatusKey,
  ]);

  return { url, source, isLoading, error };
}

export default usePlayableUrl;

/**
 * Non-reactive function to resolve a playable URL for preloading.
 *
 * Unlike the hook, this function can be called imperatively without
 * triggering re-renders. Used for preloading the next video.
 *
 * @param media - The media item to resolve URL for
 * @returns Promise with url and source
 */
export async function resolvePlayableUrl(
  media: StreamMediaItem,
): Promise<{ url: string; source: PlaybackSource }> {
  try {
    const localUrl = await mediaStore.getLocalUrlAsync(media);

    if (localUrl) {
      logger.debug(`[resolvePlayableUrl] Using cache: ${media.name}`);
      return { url: localUrl, source: 'cache' };
    }

    console.log(`[resolvePlayableUrl] Using remote: ${media.name}`);
    return { url: media.url, source: 'remote' };
  } catch (err) {
    console.error('[resolvePlayableUrl] Error, falling back to remote:', err);
    return { url: media.url, source: 'remote' };
  }
}
